import * as React from 'react';

import { Table } from '@tanstack/react-table';
import { CirclePlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  DataTableEndlessPaginated,
  EmptyStateProps,
} from '@/components/data-table/data-table-endless-paginated';
import { StickyTableStateContent } from '@/components/data-table/data-table-states';
import { Button } from '@/components/ui/button';
import { AuthenticatedRoutes } from '@/router/routes';

import { ProjectListItem } from '../../types/project';

interface ClientsTableProps {
  table: Table<ProjectListItem>;
  isLoading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  error?: string;
  isAnyFilterApplied?: boolean;
  totalCount?: number;
  handleRowClick: (project: ProjectListItem) => void;
  getRowHref?: (project: ProjectListItem) => string | undefined;
  fixedHeight?: boolean;
  tableId?: string;
  onResizeEnd?: () => void;
}

export const ProjectsTable: React.FC<ClientsTableProps> = ({
  isAnyFilterApplied,
  totalCount,
  handleRowClick,
  getRowHref,
  fixedHeight = false,
  tableId,
  onResizeEnd,
  ...paginatedTableProps
}) => {
  const { t } = useTranslation(['authenticated.projects', 'common']);
  const navigate = useNavigate();

  const renderEmptyState = ({ colSpan }: EmptyStateProps): React.ReactNode => {
    // If there's any filter but no results, show "No results found"
    if (isAnyFilterApplied) {
      return (
        <tr>
          <td colSpan={colSpan} className="h-16 w-full px-0">
            <StickyTableStateContent className="px-12 py-4">
              <div className="text-sm">{t('common:noResultsFound')}</div>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    // If there are no projects at all (total count is 0), show empty state with buttons
    if (totalCount === 0) {
      return (
        <tr>
          <td colSpan={colSpan} className="w-full px-0 py-4">
            <StickyTableStateContent className="items-start px-8 text-left">
              <div className="flex flex-col">
                <div className="text-sm font-medium">
                  {t('authenticated.projects:table.noProjectsYet')}
                </div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {t('authenticated.projects:table.noProjectsDescription')}
                </div>
                <Button
                  variant="default"
                  size="sm"
                  className="mt-3 h-9 w-max gap-2"
                  onClick={() => navigate(AuthenticatedRoutes.AddProject)}
                >
                  <CirclePlus className="h-4 w-4" />
                  {t('authenticated.projects:addProject')}
                </Button>
              </div>
            </StickyTableStateContent>
          </td>
        </tr>
      );
    }

    return null;
  };

  return (
    <DataTableEndlessPaginated
      {...paginatedTableProps}
      renderEmptyState={renderEmptyState}
      onRowClick={handleRowClick}
      getRowHref={getRowHref}
      fixedHeight={fixedHeight}
      tableId={tableId}
      onResizeEnd={onResizeEnd}
    />
  );
};
